import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import ModalShell from '../../Attendance/Component/ModalShell';
import { endShiftAssignment } from '../../../../redux/actions/SocietyPortal/ShiftAction/ShiftAction';
import { todayISO, formatDateOnly } from '../../../../consts/shiftConstants';

// Close an open assignment — POST /api/shift-assignments/:id/end.
//
// This is the right tool for anything that has already happened; DELETE is for
// an assignment created by mistake. Ending leaves the period on record with the
// shift it was actually worked on.
const EndAssignmentModal = ({ assignment, onClose, onDone }) => {
    const dispatch = useDispatch();
    const { assignmentMutationLoading } = useSelector(state => state.ShiftReducer || {});

    const [effectiveTo, setEffectiveTo] = useState(todayISO());
    const [error, setError] = useState('');

    useEffect(() => {
        if (!assignment) return;
        setEffectiveTo(todayISO());
        setError('');
    }, [assignment]);

    if (!assignment) return null;

    const employeeName = [assignment.employee?.first_name, assignment.employee?.last_name]
        .filter(Boolean).join(' ').trim() || `Employee #${assignment.employee_id}`;

    const handleSubmit = () => {
        if (!effectiveTo) return setError('Pick an end date');
        if (effectiveTo < assignment.effective_from) {
            return setError(`Cannot end before the assignment started (${formatDateOnly(assignment.effective_from)})`);
        }
        dispatch(endShiftAssignment({
            assignment_id: assignment.assignment_id,
            effective_to: effectiveTo,
            onSuccess: (data) => { onClose(); onDone?.(data); },
        }));
    };

    return (
        <ModalShell
            isOpen={Boolean(assignment)}
            title="End this assignment"
            icon="mdi:calendar-end"
            onClose={onClose}
            busy={assignmentMutationLoading}
            actions={
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={assignmentMutationLoading}
                    className="tw-px-3 tw-py-1.5 tw-rounded-md tw-bg-blue-600 tw-text-white tw-text-xs tw-font-medium hover:tw-bg-blue-700 disabled:tw-opacity-50"
                >
                    {assignmentMutationLoading ? 'Ending…' : 'End assignment'}
                </button>
            }
        >
            <p className="tw-text-sm tw-text-gray-600 tw-mb-3">
                <strong>{employeeName}</strong> stops working the{' '}
                <strong>{assignment.shift?.shift_name || 'assigned'}</strong> shift after this date.
            </p>

            <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Last working date *</label>
            <input
                type="date"
                value={effectiveTo}
                min={assignment.effective_from}
                onChange={(e) => { setEffectiveTo(e.target.value); setError(''); }}
                className={`tw-w-full tw-border tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-1 ${error ? 'tw-border-red-300 focus:tw-ring-red-400' : 'tw-border-gray-200 focus:tw-ring-blue-500'}`}
            />
            {error && <p className="tw-text-[11px] tw-text-red-500 tw-mt-1 tw-mb-0">{error}</p>}


            <p className="tw-text-[11px] tw-text-gray-500 tw-bg-gray-50 tw-rounded-md tw-px-3 tw-py-2 tw-mt-3 tw-mb-0 tw-flex tw-items-start tw-gap-1.5">
                <Icon icon="mdi:information-outline" className="tw-mt-0.5 tw-flex-shrink-0" />
                To move this employee onto a different shift instead, use <strong className="tw-mx-1">Change shift</strong> — it ends this assignment and opens the new one in one step.
            </p>
        </ModalShell>
    );
};

export default EndAssignmentModal;
